import React, { useState, useEffect, useMemo } from 'react'
import Layout from '../../Component/Layout'
import Bd from './Bd';
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import Select from "react-select";
import { ORDER_API, MASTER_API } from '../../apiConfig'

// --- Shared dropdown option/menu styling (matches Billing.js / Sales Order dropdown look) ---
const dropdownOptionStyle = {
    control: (base) => ({
        ...base,
        minHeight: 32,
        height: 32,
        fontSize: '13px',
        borderColor: '#dee2e6',
        boxShadow: 'none',
        '&:hover': { borderColor: '#b3b3b3' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: '32px', display: 'flex', alignItems: 'center' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({ ...base, fontSize: '13px', zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : 'white',
        color: state.isSelected ? 'white' : '#334155',
        cursor: 'pointer',
        fontSize: '13px'
    })
};

function BillingReview() {
    const [salesOrganization, setSalesOrganization] = useState("");
    const [division, setDivision] = useState("");
    const [dChannel, setDChannel] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState("");
    const [rfqSummary, setRfqSummary] = useState([]);
    const [reviewTab, setReviewTab] = useState("active");
    //eslint-disable-next-line
    const [selectedRows, setSelectedRows] = useState([]);
    const [modalData, setModalData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    //eslint-disable-next-line
    const [modalType, setModalType] = useState('');
    const [productDetails, setProductDetails] = useState(null);
    const [showProductModal, setShowProductModal] = useState(false);
    const [activeTab, setActiveTab] = useState("basic");
    //eslint-disable-next-line
    const [expandedSoId, setExpandedSoId] = useState(null);
    //eslint-disable-next-line
    const [expandedProductSoId, setExpandedProductSoId] = useState(null);
    //eslint-disable-next-line
    const [expandedDocsSoId, setExpandedDocsSoId] = useState(null);
    const [biId, setBiId] = useState("");
    const [showPaymentTermsModal, setShowPaymentTermsModal] = useState(false);

    // --- MASTER DATA (from configuration, not derived from loaded rows) ---
    const [salesOrgList, setSalesOrgList] = useState([]);
    const [customerSegmentList, setCustomerSegmentList] = useState([]);
    const [channelList, setChannelList] = useState([]);
    const [customerList, setCustomerList] = useState([]);

    useEffect(() => {
        const fetchMasters = async () => {
            try {
                const [orgRes, segRes, chnRes, custRes] = await Promise.all([
                    fetch(`${MASTER_API}/configuration/sales-organization`),
                    fetch(`${MASTER_API}/configuration/dynamic/customer-segment`),
                    fetch(`${MASTER_API}/configuration/distribution-channel`),
                    fetch(`${ORDER_API}/billing/customers`)
                ]);
                const [org, seg, chn, cust] = await Promise.all([
                    orgRes.json(), segRes.json(), chnRes.json(), custRes.json()
                ]);
                setSalesOrgList(org.data || org || []);
                setCustomerSegmentList(seg.data || seg || []);
                setChannelList(chn.data || chn || []);
                setCustomerList(cust.data || cust || []);
            } catch (err) {
                console.error('Failed to load Billing Review master data:', err);
            }
        };
        fetchMasters();
    }, []);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                const res = await fetch(`${ORDER_API}/billing/summary/review`);
                const data = await res.json();
                setRfqSummary(data);
            } catch (err) {
                console.error('Failed to load Billing summary:', err);
            }
        };
        fetchSummary();
    }, []);

    const dropdownOptions = useMemo(() => {
        const getUnique = (key) => [...new Set(rfqSummary.map(item => item[key]).filter(Boolean))];
        return {
            // Billing IDs still come from the loaded rows themselves (not a master table)
            biIds: getUnique('bi_id')
        };
    }, [rfqSummary]);

    const filteredData = useMemo(() => {
        return rfqSummary.filter((item) => {
            const status = item.status || "";
            const tabMatch =
                reviewTab === 'active' ? status === 'Submitted' :
                    reviewTab === 'approval' ? (status !== 'Submitted' && status !== 'Submitted to Customer') :
                        reviewTab === 'customer' ? status === 'Submitted to Customer' :
                            true;
            return (
                tabMatch &&
                (salesOrganization === "" || item.sales_organization === salesOrganization) &&
                (division === "" || item.division === division) &&
                (dChannel === "" || item.distribution_channel === dChannel) &&
                (selectedCustomer === "" || item.customer === selectedCustomer) &&
                (biId === "" || (item.bi_id || "").includes(biId))
            );
        });
    }, [rfqSummary, reviewTab, salesOrganization, division, dChannel, selectedCustomer, biId]);


    useEffect(() => {
        if (showModal) {
            setActiveTab("product"); // Default to Product tab like Billing.js
            setExpandedSoId(null);
            setExpandedProductSoId(null);
            setExpandedDocsSoId(null);
        }
    }, [showModal, modalData]);

    // --- Helper Component for Info Fields ---
    const InfoField = ({ label, value }) => (
        <div style={{ marginBottom: '10px' }}>
            <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '700',
                color: '#64748b',
                textTransform: 'uppercase',
                marginBottom: '2px'
            }}>
                {label}
            </label>
            <div style={{
                fontSize: '13px',
                fontWeight: '500',
                color: '#1e293b',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            }}>
                {value || "-"}
            </div>
        </div>
    );

    const renderProductContent = (item) => {
        // --- Calculate Totals ---
        const totals = item.productDetails?.reduce((acc, curr) => {
            const igst = parseFloat(curr.igst_amount) || 0;
            const rawTotalGst = parseFloat(curr.total_gst) || 0;
            const rawGrandTotal = parseFloat(curr.final_price_after_gst) || 0;
            const grossRow = (parseFloat(curr.unit_price) || 0) * (parseFloat(curr.delivery_qty) || 0);

            return {
                totalValue: acc.totalValue + grossRow,
                netPrice: acc.netPrice + (parseFloat(curr.net_price_after_discount) || 0),
                cgst: acc.cgst + (parseFloat(curr.cgst_amount) || 0),
                sgst: acc.sgst + (parseFloat(curr.sgst_amount) || 0),
                igst: acc.igst + igst,
                totalGst: acc.totalGst + rawTotalGst,
                grandTotal: acc.grandTotal + rawGrandTotal
            };
        }, { totalValue: 0, netPrice: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0, grandTotal: 0 });

        return (
            <div style={{ ...styles.tableContainer, overflowX: 'auto', marginTop: '0', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <table className="table table-hover mb-0" style={{ ...styles.erpTable, width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f8fafc' }}>
                            <th style={{ ...styles.erpTh, textAlign: "center", width: '50px' }}>Item</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Product Code</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Description</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>UOM</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Unit Price</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Qty</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Del. Qty</th>

                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>Total Value</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>Disc %</th>

                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>Net Price</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>CGST</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>SGST</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>IGST</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>Total GST</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>Grand Total</th>

                            <th style={styles.erpTh}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {item.productDetails?.length === 0 ? (
                            <tr><td colSpan={16} style={{ ...styles.erpTd, textAlign: "center", padding: '20px' }}>No Products Found</td></tr>
                        ) : (
                            <>
                                {item.productDetails?.map((prod, idx) => {
                                    const grossRow = (parseFloat(prod.unit_price) || 0) * (parseFloat(prod.delivery_qty) || 0);
                                    const igstRow = parseFloat(prod.igst_amount) || 0;
                                    const totalGstRow = parseFloat(prod.total_gst) || 0;
                                    const grandTotalRow = parseFloat(prod.final_price_after_gst) || 0;

                                    return (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ ...styles.erpTd, textAlign: "center" }}>{idx + 1}</td>
                                            <td style={styles.erpTd}>{prod.product_code}</td>
                                            <td style={styles.erpTd}>{prod.description}</td>
                                            <td style={styles.erpTd}>{prod.uom}</td>
                                            <td style={styles.erpTd}>{prod.unit_price}</td>
                                            <td style={styles.erpTd}>{prod.quantity}</td>
                                            <td style={styles.erpTd}>{prod.delivery_qty}</td>

                                            {/* Data Cells */}
                                            <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: '500' }}>{grossRow.toFixed(2)}</td>
                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>{prod.discount || 0}</td>

                                            {/* Financial Cells */}
                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(parseFloat(prod.net_price_after_discount || 0).toFixed(2))}</td>
                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(parseFloat(prod.cgst_amount || 0).toFixed(2))}</td>
                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(parseFloat(prod.sgst_amount || 0).toFixed(2))}</td>
                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(igstRow.toFixed(2))}</td>
                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totalGstRow.toFixed(2))}</td>
                                            <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: '600' }}>{formatCurrency(grandTotalRow.toFixed(2))}</td>

                                            <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                <button
                                                    className="btn btn-sm btn-outline-dark"
                                                    style={{ fontSize: '11px', padding: '2px 10px' }}
                                                    onClick={() => handleViewProductDetails(prod.id)}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {/* Summary Row */}
                                <tr style={{ backgroundColor: '#f1f5f9', borderTop: '2px solid #e2e8f0', fontWeight: '700' }}>
                                    <td colSpan={7} style={{ ...styles.erpTd, textAlign: 'right', color: '#64748b' }}>TOTAL:</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.totalValue.toFixed(2))}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>-</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.netPrice.toFixed(2))}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.cgst.toFixed(2))}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.sgst.toFixed(2))}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.igst.toFixed(2))}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.totalGst.toFixed(2))}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a', fontSize: '13px' }}>{formatCurrency(totals.grandTotal.toFixed(2))}</td>
                                    <td></td>
                                </tr>
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderDocsContent = (item) => (
        <div className="list-group mt-3">
            {item.pdfFilePath && (
                <div className="list-group-item d-flex justify-content-between align-items-center"
                    style={{ backgroundColor: "#e9f7ef", borderLeft: "4px solid #198754" }}>
                    <div>
                        <i className="bi bi-file-earmark-pdf-fill text-danger me-2"></i>
                        <span style={{ fontWeight: '600' }}>
                            {item.bi_id ? `Billing Invoice (${item.bi_id})` : "Generated Billing PDF"}
                        </span>
                    </div>
                    <a href={``} download className="btn btn-success btn-sm">
                        <i className="bi bi-download me-1"></i> Download Bill
                    </a>
                </div>
            )}
            {item.documents?.map((file, idx) => (
                <div className="list-group-item d-flex justify-content-between align-items-center" key={idx}>
                    <span>{file.file_name}</span>
                    <a href={file.file_url.startsWith('http') ? file.file_url : ``} download className="btn btn-outline-success btn-sm">
                        <i className="bi bi-download"></i>
                    </a>
                </div>
            ))}
            {!item.pdfFilePath && (!item.documents || item.documents.length === 0) && (
                <div className="p-4 text-center text-muted border rounded bg-light">No documents available.</div>
            )}
        </div>
    );

    const handleViewClick = async (bi_id) => {
        try {
            const res = await fetch(`${ORDER_API}/billing/submitted/bi/${bi_id}`);
            if (!res.ok) throw new Error("Fetch failed");
            const data = await res.json();
            setModalData([data]);
            setModalType('view');
            setShowModal(true);
        } catch (error) {
            console.error("Error viewing bill:", error);
            toast.error("Failed to load billing details.");
        }
    };

    const handleViewProductDetails = async (id) => {
        try {
            const response = await fetch(`${ORDER_API}/billing/item/${id}`);
            if (response.ok) {
                const data = await response.json();
                setProductDetails(data);
                setShowProductModal(true);
            } else {
                alert("Failed to fetch product details.");
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedRows([]);
    };

    const handleSubmitToCustomer = async () => {
        const currentBiId = modalData[0]?.bi_id;
        if (!currentBiId) return;

        try {
            const response = await fetch(`${ORDER_API}/billing/${currentBiId}/submit-to-customer`, {
                method: "POST",
            });
            const result = await response.json();

            if (response.ok) {
                toast.success(result.message);

                // Refresh the summary table
                const res = await fetch(`${ORDER_API}/billing/summary/review`);
                const data = await res.json();
                setRfqSummary(data);

                handleCloseModal(); // Close modal after success
            } else {
                toast.error(result.message || "Failed to submit.");
            }
        } catch (error) {
            console.error("Error submitting to customer:", error);
            toast.error("An error occurred.");
        }
    };

    const formatCurrency = (value) => {
        const val = Number(value);
        if (isNaN(val)) return "0";
        if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
        if (val >= 100000) return `${(val / 100000).toFixed(2)} Lakh`;
        return val.toLocaleString('en-IN');
    };

    return (
        <Layout>
            <div style={styles.pageTitleBar}>
                <div>
                    <div style={styles.pageTitle}>Billing Review</div>
                    <div style={styles.pageSubtitle}>Manage and view your data here</div>
                </div>
            </div>

            <div style={styles.filterCard}>
                <div className="row g-3">
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Sales Organization</label>
                        <Select
                            options={salesOrgList.map(item => ({ value: item.code || item.name || item.sales_organization, label: item.code || item.name || item.sales_organization }))}
                            value={salesOrganization ? { value: salesOrganization, label: salesOrganization } : null}
                            onChange={opt => setSalesOrganization(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Customer Segment</label>
                        <Select
                            options={customerSegmentList.map(item => ({ value: item.name || item.code || item.customer_segment || item, label: item.name || item.code || item.customer_segment || item }))}
                            value={division ? { value: division, label: division } : null}
                            onChange={opt => setDivision(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Distribution Channel</label>
                        <Select
                            options={channelList.map(item => ({ value: item.code || item.name || item.distribution_channel, label: item.code || item.name || item.distribution_channel }))}
                            value={dChannel ? { value: dChannel, label: dChannel } : null}
                            onChange={opt => setDChannel(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Billing ID</label>
                        <Select
                            options={dropdownOptions.biIds.map(opt => ({ value: opt, label: opt }))}
                            value={biId ? { value: biId, label: biId } : null}
                            onChange={opt => setBiId(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-4 col-sm-12">
                        <label style={styles.filterLabel}>Customer</label>
                        <Select
                            options={customerList.map(c => ({
                                value: `${c.partner_code} - ${c.business_partner_name}`,
                                label: `${c.partner_code} - ${c.business_partner_name}`
                            }))}
                            value={selectedCustomer ? { value: selectedCustomer, label: selectedCustomer } : null}
                            onChange={opt => setSelectedCustomer(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select Customer"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                </div>
            </div>

            {/* ===================== ACTIVE / APPROVAL / CUSTOMER TOGGLE ===================== */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "14px" }}>
                <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: "8px", padding: "3px", gap: "2px" }}>
                    {[
                        { key: "active", label: "Active" },
                        { key: "approval", label: "Approval" },
                        { key: "customer", label: "Customer" },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setReviewTab(tab.key)}
                            style={{
                                border: "none", borderRadius: "6px", padding: "6px 18px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
                                background: reviewTab === tab.key ? "#ffffff" : "transparent",
                                color: reviewTab === tab.key ? "#1e293b" : "#64748b",
                                boxShadow: reviewTab === tab.key ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                                transition: "all 0.15s ease"
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Table */}
            <div style={styles.tableContainer}>
                <table className="table" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={{ ...styles.erpTh, width: 150 }}>Billing ID</th>
                            <th style={{ ...styles.erpTh, width: 150 }}>Customer</th>
                            <th style={{ ...styles.erpTh, width: 150 }}>Billing Date</th>
                            <th style={{ ...styles.erpTh, width: 150 }}>Requirement Date</th>
                            <th style={{ ...styles.erpTh, width: 150 }}>Total Price</th>
                            <th style={{ ...styles.erpTh, width: 150 }}>Status</th>
                            <th style={{ ...styles.erpTh, width: 100 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length === 0 ? (
                            <tr><td colSpan="7" style={{ ...styles.erpTh, textAlign: "center" }}>No Records Found</td></tr>
                        ) : (
                            filteredData.map((item) => (
                                <tr key={item.so_id} style={{ backgroundColor: item.status === 'approved' ? "#f2f2f2" : "white", opacity: item.status === 'approved' ? 0.6 : 1 }}>
                                    <td style={styles.erpTd}>{item.bi_id ? (
                                        <span
                                            onClick={() => handleViewClick(item.bi_id)}
                                            style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
                                        >
                                            {item.bi_id}
                                        </span>
                                    ) : ""}</td>
                                    <td style={styles.erpTd}>{item.customer || ""}</td>
                                    {/* <td style={styles.erpTd}>{item.bi_date ? new Date(item.bi_date).toLocaleDateString("en-GB") : ""}</td>
                                    <td style={styles.erpTd}>{item.requirement_date ? new Date(item.requirement_date).toLocaleDateString("en-GB") : "N/A"}</td> */}
                                    <td style={styles.erpTd}>
                                        {item.bi_date ? (() => {
                                            const d = new Date(item.bi_date);
                                            const day = String(d.getDate()).padStart(2, '0');
                                            const month = String(d.getMonth() + 1).padStart(2, '0');
                                            const year = d.getFullYear();
                                            return `${day}/${month}/${year}`;
                                        })() : ""}
                                    </td>
                                    <td style={styles.erpTd}>
                                        {item.requirement_date ? (() => {
                                            const d = new Date(item.requirement_date);
                                            const day = String(d.getDate()).padStart(2, '0');
                                            const month = String(d.getMonth() + 1).padStart(2, '0');
                                            const year = d.getFullYear();
                                            return `${day}/${month}/${year}`;
                                        })() : "N/A"}
                                    </td>
                                    <td style={styles.erpTd}>{item.total_price}</td>
                                    <td style={styles.erpTd}>{item.status}</td>
                                    <td style={styles.erpTd}>
                                        <button className="btn btn-outline-dark btn-sm" onClick={() => handleViewClick(item.bi_id)}>View</button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* --- MAIN MODAL --- */}
            {showModal && modalData.length > 0 && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "1200px", width: "95%", maxHeight: "95vh" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#1e293b' }}>
                                {`View Bill: ${modalData[0]?.bi_id || ""}`}
                            </div>
                            <button style={styles.iconBtn} onClick={handleCloseModal}>✕</button>
                        </div>

                        <div style={styles.modalBody}>
                            {/* --- HEADER SUMMARY SECTION (Information Card) --- */}
                            <div className="p-3 mb-3" style={{ backgroundColor: '#f1f5f9', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <div className="row">
                                    <div className="col-md-4">
                                        <InfoField label="Customer" value={modalData[0]?.customer} />
                                    </div>
                                    <div className="col-md-3">
                                        {/* Map correct field names for review data */}
                                        <InfoField label="Sales Org" value={modalData[0]?.sales_organization || modalData[0]?.salesOrganization} />
                                    </div>
                                    <div className="col-md-3">
                                        <InfoField label="Customer Segment" value={modalData[0]?.division} />
                                    </div>
                                    <div className="col-md-2">
                                        <InfoField label="Dist. Channel" value={modalData[0]?.distribution_channel || modalData[0]?.distributionChannel} />
                                    </div>
                                </div>
                            </div>

                            {/* --- DETAILS GRID --- */}
                            <div className="row g-3 mb-4">
                                {/* Addresses */}
                                <div className="col-md-8">
                                    <div className="p-3 h-100" style={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                        <div className="row">
                                            <div className="col-6 border-end">
                                                <label className="text-muted fw-bold" style={{ fontSize: '11px' }}>SHIPPING ADDRESS</label>
                                                <div style={{ fontSize: '13px', color: '#334155', whiteSpace: 'pre-wrap' }}>{modalData[0]?.shipToAddress || ""}</div>
                                            </div>
                                            <div className="col-6 ps-3">
                                                <label className="text-muted fw-bold" style={{ fontSize: '11px' }}>BILLING ADDRESS</label>
                                                <div style={{ fontSize: '13px', color: '#334155', whiteSpace: 'pre-wrap' }}>{modalData[0]?.billToAddress || ""}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Financials */}
                                <div className="col-md-4">
                                    <div className="p-3 h-100" style={{ border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff' }}>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span style={{ fontSize: '12px', color: '#64748b' }}>GSTIN</span>
                                            <span style={{ fontSize: '13px', fontWeight: '500' }}>{modalData[0]?.gstin || ""}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span style={{ fontSize: '12px', color: '#64748b' }}>Account No</span>
                                            <span style={{ fontSize: '13px', fontWeight: '500' }}>{modalData[0]?.accountNumber || ""}</span>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span style={{ fontSize: '12px', color: '#64748b' }}>IFSC Code</span>
                                            <span style={{ fontSize: '13px', fontWeight: '500' }}>{modalData[0]?.ifscCode || ""}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* --- PAYMENT TERMS (own row, Billing.js style) --- */}
                            <div className="mb-4">
                                <label style={styles.compactLabel}>Payment Terms</label>
                                <div className="d-flex gap-2">
                                    <div
                                        className="form-control bg-light"
                                        style={{ ...styles.compactInput, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {modalData[0]?.paymentTerms || ""}
                                    </div>
                                    <button
                                        className="btn btn-dark btn-sm"
                                        onClick={() => setShowPaymentTermsModal(true)}
                                    >
                                        View
                                    </button>
                                </div>
                            </div>

                            {/* --- TABS --- */}
                            <div className="d-flex border-bottom mb-3">
                                <button
                                    className={`btn btn-sm pb-2 rounded-0 ${activeTab === 'product' ? 'border-bottom border-dark fw-bold text-dark' : 'text-muted'}`}
                                    style={{ border: 'none', background: 'none', paddingLeft: '0', marginRight: '20px' }}
                                    onClick={() => setActiveTab('product')}
                                >
                                    Product
                                </button>
                                <button
                                    className={`btn btn-sm pb-2 rounded-0 ${activeTab === 'feasibility' ? 'border-bottom border-dark fw-bold text-dark' : 'text-muted'}`}
                                    style={{ border: 'none', background: 'none', marginRight: '20px' }}
                                    onClick={() => setActiveTab('feasibility')}
                                >
                                    Review
                                </button>
                                <button
                                    className={`btn btn-sm pb-2 rounded-0 ${activeTab === 'docs' ? 'border-bottom border-dark fw-bold text-dark' : 'text-muted'}`}
                                    style={{ border: 'none', background: 'none' }}
                                    onClick={() => setActiveTab('docs')}
                                >
                                    Document
                                </button>
                            </div>

                            {/* --- TAB CONTENT --- */}
                            <div>
                                {activeTab === "product" && modalData.map((item) => (
                                    <div key={item.so_id || Math.random()} className="mb-3">
                                        <div style={{ padding: "10px", border: "1px solid #ddd", borderRadius: "8px", backgroundColor: "#fff" }}>
                                            {renderProductContent(item)}
                                        </div>
                                    </div>
                                ))}

                                {activeTab === "docs" && modalData.map((item) => (
                                    <div key={item.so_id || Math.random()} className="mb-3">
                                        <div style={{ padding: "10px", border: "1px solid #ddd", borderRadius: "8px", backgroundColor: "#fff" }}>
                                            {renderDocsContent(item)}
                                        </div>
                                    </div>
                                ))}

                                {activeTab === "feasibility" && (
                                    <div>
                                        <Bd productDetails={modalData[0].productDetails} selectedRfq={modalData[0]} />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={styles.modalFooter}>
                            {modalData[0]?.status === 'Approved' && (
                                <button
                                    className="btn btn-dark btn-sm"
                                    onClick={handleSubmitToCustomer}
                                    style={{ marginRight: '10px' }}
                                >
                                    Submit to Customer
                                </button>
                            )}
                            <button className="btn btn-outline-secondary btn-sm" onClick={handleCloseModal}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Terms Detail Modal (Billing.js style) */}
            {showPaymentTermsModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "700px" }}>
                        <div style={styles.modalHeader}>
                            <strong>Payment Terms</strong>
                            <button style={styles.iconBtn} onClick={() => setShowPaymentTermsModal(false)}>✕</button>
                        </div>
                        <div style={styles.modalBody}>
                            <div style={{ fontSize: '13px', color: '#334155', whiteSpace: 'pre-wrap' }}>
                                {modalData[0]?.paymentTerms || ""}
                            </div>
                        </div>
                        <div style={styles.modalFooter}>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setShowPaymentTermsModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Product View Details Modal */}
            {showProductModal && productDetails && (
                <div style={styles.modalBackdrop}>
                    <div
                        style={{
                            ...styles.modal,
                            maxWidth: "950px",
                            width: "95%",
                            maxHeight: "85vh",
                            padding: "14px",
                            fontSize: "13px"
                        }}
                    >


                        {/* ================= BODY ================= */}
                        <div style={{ padding: "12px" }}>

                            {/* ===== TOP ROW (LIKE PIC) ===== */}
                            <table className="table table-bordered table-sm mb-3">
                                <thead style={{ background: "#f8fafc", fontWeight: 600 }}>
                                    <tr>
                                        <th>Product Code</th>
                                        <th>HSN Code</th>
                                        <th>Quantity</th>
                                        <th>Standard Price</th>
                                        <th>Discount (%)</th>
                                        <th>Total Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{productDetails.product_code}</td>
                                        <td>{productDetails.hsn_code}</td>
                                        <td>{productDetails.delivery_qty}</td>
                                        <td>₹ {productDetails.unit_price}</td>
                                        <td>{productDetails.discount}</td>
                                        <td style={{ fontWeight: 600 }}>
                                            ₹ {productDetails.totalPrice}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* ===== NET PRICE LINE ===== */}
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "8px 10px",
                                    borderTop: "1px solid #999",
                                    borderBottom: "1px solid #999",
                                    marginBottom: "10px",
                                    fontWeight: 600
                                }}
                            >
                                <span>Net Price :</span>
                                <span>₹ {productDetails.netPrice}</span>
                            </div>

                            {/* ===== GST BREAKUP ===== */}
                            <table
                                className="table table-sm"
                                style={{ width: "50%", marginLeft: "auto" }}
                            >
                                <tbody>
                                    <tr>
                                        <td>CGST @ {productDetails.cgst}%</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {productDetails.cgstAmount}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>SGST @ {productDetails.sgst}%</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {productDetails.sgstAmount}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>IGST @ {productDetails.igst}%</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {productDetails.igstAmount}
                                        </td>
                                    </tr>
                                    <tr style={{ borderTop: "1px solid #000", fontWeight: 600 }}>
                                        <td>Total GST</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {productDetails.totalGST}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* ===== GRAND TOTAL ===== */}
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "12px",
                                    marginTop: "8px",
                                    background: "#f1f5f9",
                                    fontWeight: 700,
                                    fontSize: "15px",
                                    borderRadius: "4px"
                                }}
                            >
                                <span>Grand Total :</span>
                                <span>₹ {productDetails.finalPriceWithGST}</span>
                            </div>
                        </div>

                        {/* ================= FOOTER ================= */}
                        <div style={{ ...styles.modalFooter, padding: "10px 14px" }}>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setShowProductModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} />
        </Layout>
    );
}

const styles = {
    compactLabel: { fontSize: "12px", fontWeight: 600, marginBottom: "2px", color: '#334155' },
    compactInput: { fontSize: "13px", height: "34px", padding: "4px 8px" },
    compactTabBtn: { fontSize: "13px", padding: "4px 10px" },
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
    erpTh: { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
    erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
    filterCard: { background: '#ffffff', borderRadius: 8, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 18 },
    filterLabel: { fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#334155' },
    filterSelect: { height: 36, fontSize: 13 },
    pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    pageTitle: { fontSize: 20, fontWeight: 700, color: '#1e293b' },
    pageSubtitle: { fontSize: 12, color: '#64748b' },
    primaryBtn: { paddingLeft: 14, paddingRight: 14 },
    modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '20px' },
    modal: { background: '#fff', borderRadius: '8px', width: '90%', maxWidth: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    modalHeader: { padding: '15px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    iconBtn: { background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer' },
    modalBody: { padding: '20px', overflowY: 'auto', flex: 1 },
    modalFooter: { padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
    tabs: { display: 'flex', gap: 8, margin: '14px 0' },
};

export default BillingReview;